import { Routes } from '@angular/router';
import { authGuard, permissionGuard, superAdminGuard, roleGuard } from './core/auth/guards/auth.guard';
import { RoleName } from './core/auth/models/role.model';

export const routes: Routes = [
  { 
    path: 'login',
    loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'verify-email',
    loadComponent: () => import('./features/auth/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
  },
  {
    path: '',
    loadComponent: () => import('./layout/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'entities',
        loadComponent: () => import('./features/entities/entities.component').then(m => m.EntitiesComponent),
      },
      {
        path: 'entities/add',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./features/entities/add-entity/add-entity.component').then(m => m.AddEntityComponent),
      },
      {
        path: 'entities/edit/:id',
        canActivate: [roleGuard([RoleName.SUPER_ADMIN, RoleName.ENTITY_ADMIN])],
        loadComponent: () => import('./features/entities/edit-entity/edit-entity.component').then(m => m.EditEntityComponent),
      },
      {
        path: 'entities/view/:id',
        loadComponent: () => import('./features/entities/view-entity/view-entity.component').then(m => m.ViewEntityComponent),
      },
      {
        path: 'clinics/add',
        canActivate: [permissionGuard(['MANAGE_CLINICS'])],
        loadComponent: () => import('./features/clinics/add-clinic/add-clinic.component').then(m => m.AddClinicComponent),
      },
      {
        path: 'clinics/edit/:id',
        canActivate: [permissionGuard(['UPDATE_CLINICS'])],
        loadComponent: () => import('./features/clinics/edit-clinic/edit-clinic.component').then(m => m.EditClinicComponent),
      },
      {
        path: 'clinics/view/:id',
        loadComponent: () => import('./features/clinics/view-clinic/view-clinic.component').then(m => m.ViewClinicComponent),
      },
      {
        path: 'clinics',
        loadComponent: () => import('./features/clinics/clinics.component').then(m => m.ClinicsComponent),
      },
      {
        path: 'patients/add',
        canActivate: [permissionGuard(['MANAGE_PATIENTS'])],
        loadComponent: () => import('./features/patients/add-patient/add-patient.component').then(m => m.AddPatientComponent),
      },
      {
        path: 'patients/edit/:id',
        canActivate: [permissionGuard(['MANAGE_PATIENTS'])],
        loadComponent: () => import('./features/patients/edit-patient/edit-patient.component').then(m => m.EditPatientComponent),
      },
      {
        path: 'patients/view/:id',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/patients/view-patient/view-patient.component').then(m => m.ViewPatientComponent),
      },
      {
        path: 'patients/patient-data',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/patients/patient-data').then(m => m.PatientDataComponent),
      },
      {
        path: 'patients/patient-data/spectral-viewer',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/spectral-viewer/spectral-viewer.component').then(m => m.SpectralViewerComponent),
      },
      {
        path: 'patients',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/patients/patients.component').then(m => m.PatientsComponent),
      },
      {
        path: 'users/add',
        canActivate: [permissionGuard(['MANAGE_USERS'])],
        loadComponent: () => import('./features/users/add-user/add-user.component').then(m => m.AddUserComponent),
      },
      {
        path: 'users/edit/:id',
        canActivate: [permissionGuard(['MANAGE_USERS'])],
        loadComponent: () => import('./features/users/edit-user/edit-user.component').then(m => m.EditUserComponent),
      },
      {
        path: 'users/view/:id',
        canActivate: [permissionGuard(['MANAGE_USERS'])],
        loadComponent: () => import('./features/users/view-user/view-user.component').then(m => m.ViewUserComponent),
      },
      {
        path: 'users',
        canActivate: [permissionGuard(['MANAGE_USERS'])],
        loadComponent: () => import('./features/users/users.component').then(m => m.UsersComponent),
      },
      {
        path: 'reports',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/reports/reports.component').then(m => m.ReportsComponent),
      },
      {
        path: 'reports/patient-report',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/reports/patient-report/patient-report.component').then(m => m.PatientReportComponent),
      },
      {
        path: 'reports/audit-report',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./features/reports/audit-report/audit-report.component').then(m => m.AuditReportComponent),
      },
      {
        path: 'reports/usage-report',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./features/reports/usage-report/usage-report.component').then(m => m.UsageReportComponent),
      },
      {
        path: 'settings',
        canActivate: [roleGuard([RoleName.SUPER_ADMIN, RoleName.ENTITY_ADMIN])],
        loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'security',
        canActivate: [roleGuard([RoleName.ENTITY_ADMIN, RoleName.CLINIC_ADMIN, RoleName.STANDARD_USER])],
        loadComponent: () => import('./features/settings/mfa-settings/mfa-settings.component').then(m => m.MFASettingsComponent),
      },
      {
        path: 'settings/mfa',
        redirectTo: 'security',
        pathMatch: 'full',
      },
      {
        path: 'spectral-viewer',
        canActivate: [permissionGuard(['VIEW_PATIENTS'])],
        loadComponent: () => import('./features/spectral-viewer/spectral-viewer.component').then(m => m.SpectralViewerComponent),
      },
      {
        path: 'unauthorized',
        loadComponent: () => import('./features/auth/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
